import { forwardRef, HTMLAttributes, useCallback, useRef } from 'react'
import { TransitionStatus } from 'react-transition-group'
import { useClickAway } from 'react-use'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Socials from '@/components/base/socials'
import Typography from '@/components/base/typography'
import TypographyMask from '@/components/base/typography-mask'
import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'
import { useLocaleStore } from '@/store/locale'
import { stripSlash } from '@/utils/path'

import { cn } from '@/lib/utils'

export type MenuProps = HTMLAttributes<HTMLDivElement> & {
  state: TransitionStatus
}

const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ state, className, ...props }, ref) => {
    const { menu, strings } = useStore()
    const { asPath, defaultLocale } = useRouter()
    const panel = useRef<HTMLDivElement>(null!)
    const { brand, links, infos, labels } = strings || {}
    const currentLocale = useLocaleStore((state) => state.currentLocale)
    const setLayoutState = useLayoutStore((state) => state.setLayoutState)

    const isActive = useCallback(
      (url: string) =>
        stripSlash(url) ===
        stripSlash(
          `${currentLocale !== defaultLocale ? `/${currentLocale}` : ''}${asPath}`
        ),
      [asPath, defaultLocale, currentLocale]
    )

    useClickAway(panel, () => setLayoutState({ menuOpen: false }))

    return (
      <div
        ref={ref}
        className={cn(
          'static',
          'before:fixed before:left-0 before:top-0 before:z-menu before:h-full before:w-full before:bg-dark/30',
          'before:transition-opacity before:duration-500 before:ease-in-out-cubic',
          {
            'before:opacity-0 before:duration-0': state === 'entering',
            'before:opacity-100': state === 'entered',
            'before:opacity-0': state === 'exiting'
          }
        )}
      >
        <div
          ref={panel}
          className={cn(
            'fixed left-0 top-0 z-menu h-full w-full bg-light lg:w-auto',
            'flex',
            'transition-transform duration-500 ease-in-out-cubic',
            {
              '-translate-x-full duration-0': state === 'entering',
              'translate-x-0': state === 'entered',
              '-translate-x-full': state === 'exiting'
            },
            className
          )}
          {...props}
        >
          <div className="flex grow flex-col justify-between px-margin py-[7.407vh]">
            <div className="flex">
              <button
                className={cn(
                  // 'absolute left-0 top-1/2 -mx-[1rem] -translate-y-1/2',
                  'group -m-[1rem] flex items-center p-[1rem] font-gillsans uppercase md:space-x-[1.5rem]'
                )}
                onClick={() => setLayoutState({ menuOpen: false })}
              >
                <span
                  className={cn(
                    'relative hidden flex-col md:flex',
                    'before:block before:h-[1px] before:w-[6rem] before:bg-current',
                    'before:transition-transform before:delay-75 before:duration-500 before:ease-out-cubic',
                    'group-hover:before:-translate-x-1/2 group-hover:before:scale-x-0 group-hover:before:delay-0'
                  )}
                >
                  <span
                    className={cn(
                      'absolute left-0 top-0 flex flex-col',
                      'before:block before:h-[1px] before:w-[6rem] before:translate-x-1/2 before:scale-x-0 before:bg-current',
                      'before:transition-transform before:delay-0 before:duration-500 before:ease-out-cubic',
                      'group-hover:before:translate-x-0 group-hover:before:scale-x-100 group-hover:before:delay-75'
                    )}
                  />
                </span>
                <TypographyMask>
                  <Typography variant="label">{labels?.close}</Typography>
                </TypographyMask>
              </button>
            </div>

            <nav
              className={cn('grid gap-y-[1.6rem]', 'text-center lg:text-left')}
            >
              <Typography
                variant="label"
                paragraph
                className="font-gillsans uppercase text-dark/30"
              >
                {labels?.menu}
              </Typography>

              <ul className="grid gap-y-[0.8rem]">
                {menu?.main_menu?.menu.map(({ url, title }, index) => (
                  <li key={`menu-item-${index}`}>
                    <Link
                      href={url}
                      scroll={false}
                      className={cn(
                        'text-dark',
                        'transition-colors duration-300 ease-out-cubic',
                        {
                          'pointer-events-none text-copper': isActive(url)
                        },
                        'hover:text-copper'
                      )}
                    >
                      <Typography variant="h5" className="font-timesnow">
                        {title}
                      </Typography>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div
              className={cn('grid gap-y-[1.6rem]', 'text-center lg:text-left')}
            >
              <Typography
                variant="label"
                paragraph
                className="font-gillsans uppercase text-dark/30"
              >
                {labels?.info}
              </Typography>

              <p className="font-gillsans font-light">
                <Typography variant="small">{brand}</Typography>
                <br />
                <Typography variant="small">{infos?.address}</Typography>
                <br />
                <a
                  href={links?.phone.url}
                  target={links?.phone.target}
                  className="text-label transition-colors duration-300 ease-out-cubic hover:text-copper"
                >
                  {infos?.phone}
                </a>
                <br />
                <a
                  href={links?.email.url}
                  target={links?.email.target}
                  className="text-label transition-colors duration-300 ease-out-cubic hover:text-copper"
                >
                  {infos?.email}
                </a>
              </p>
            </div>

            <div className="flex justify-center lg:justify-start">
              <Socials list={menu?.social_menu} theme="dark" />
            </div>
          </div>
          <div className="relative hidden aspect-[240/810] lg:block">
            <Image
              src="/assets/image-11.jpg"
              alt=""
              fill
              className="object-cover"
            />
          </div>
        </div>
      </div>
    )
  }
)

export default Menu
